import { Alert, Col, Flex, Form, FormProps, Input, Modal, Row, Space, Switch, Table, Tooltip } from 'antd';
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Breadcrumb, Button, Container } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { ControlPanelManager } from '../../../api/ControlPanelManager';
import { useToasts } from '../../../contexts/toasts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import IconButton from '../../../components/common/IconButton';
import AppSearch from '../../../components/common/AppSearch';
import { useFullScreenLoader } from '../../../contexts/fullScreenLoader';
import { cloneDeep } from 'lodash';
import { User } from '../../../models/User';
import { CustomBootstrapLinkType } from '../../../utils';

const layoutProps: FormProps = {
  labelAlign: 'left',
  labelCol: {
    xs: 24,
    lg: 8,
  },
  wrapperCol: {
    xs: 24,
    lg: 16,
  },
};

export default function UsersList() {
  const [searchText, setSearchText] = useState('');
  const [users, setUsers] = useState<User[]>();
  const [user, setUser] = useState<User>();
  const [error, setError] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const [form] = Form.useForm();

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();

  const getUsers = useCallback(() => {
    ControlPanelManager.getUsers()
      .then(
        ({ data }) => setUsers(data),
        (err) => {
          setError(true);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('users-error', message, `Users Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [showToast]);

  const reset = useCallback(() => {
    setUsers(undefined);
    getUsers();
  }, [getUsers]);

  useEffect(reset, [reset]);

  const filteredUsers = useMemo(() => users?.filter(u => u.username.toLowerCase().includes(searchText.toLowerCase())), [searchText, users]);

  const openDeleteModal = useCallback((deleteUser: User) => () => {
    setUser(deleteUser);
    setShowDeleteModal(true);
  }, []);

  const handleClose = useCallback(() => {
    setUser(undefined);
    setShowDeleteModal(false);
    setShowPasswordModal(false);
  }, []);

  const openPasswordModal = useCallback((user: User) => () => {
    setUser(user);
    setShowPasswordModal(true);
  }, []);

  const handleSubmit = useCallback((values: any) => {
    const body = { password: values.password };
    showLoader();

    ControlPanelManager.updateUserPassword(user!.id as string, body)
      .then(
        () => {
          hideLoader();
          showToast('user-password-success', 'User password updated successfully', 'Password Updated', 'success');
          reset();
          handleClose();
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('user-password-error', message, `User Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [showLoader, hideLoader, showToast, handleClose, reset, user]);

  const toggleUser = useCallback((user: User) => (enabled: boolean) => {
    const body = { ...user, enabled };

    ControlPanelManager.updateUser(body)
      .then(
        () => {
          const newUsers = cloneDeep(users!);
          const found = newUsers.findIndex(u => u.id === user.id);
          newUsers[found].enabled = enabled;
          setUsers(newUsers);
          showToast(`user-toggle-${enabled}`, `User ${user.username} ${enabled ? 'enabled' : 'disabled'}`, 'User Updated', 'success');
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('user-toggle-error', message, `User Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [users, showToast]);

  const deleteUser = useCallback(() => {
    showLoader();

    ControlPanelManager.deleteUser(user!.id!)
      .then(
        () => {
          hideLoader();
          showToast('user-success', 'User deleted successfully', 'User Success', 'success');
          reset();
          handleClose();
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('users-error', message, `Users Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [showLoader, hideLoader, showToast, user, reset, handleClose]);

  const renderAsyncContent = useCallback(() => {
    if (error) return (
      <Alert
        type="error"
        showIcon
        message="An error occurred while retrieving users. Try again later."
      />
    )

    return (
      <Table size="middle" scroll={{ y: 'calc(100vh - 280px)' }} dataSource={filteredUsers} rowKey="id" loading={!filteredUsers}>
        <Table.Column
          key="username"
          dataIndex="username"
          title="Username"
          render={(value, record: User) => <Link to={`/control-panel/users-management/${record.id}`}>{value}</Link>}
        />
        <Table.Column
          key="email"
          dataIndex="email"
          title="Email"
          render={(value, record: User) => (
            <>
              {(value && !record.emailVerified) && (
                <Tooltip title="Email not verified">
                  <FontAwesomeIcon className="text-warning me-2" icon={solid('exclamation-circle')} />
                </Tooltip>
              )}
              <span className="text-muted">{value || '--'}</span>
            </>
          )}
        />
        <Table.Column
          key="actions"
          title="Actions"
          className="text-center"
          render={(_value, record: User) => (
            <Space size={1}>
              <div className={record.enabled ? 'text-primary' : 'text-muted'}>
                <Switch checked={record.enabled} onChange={toggleUser(record)} size="small" />
                <span className="ms-2">{record.enabled ? 'Enabled' : 'Disabled'}</span>
              </div>
              <IconButton tooltip="Change Password" className="ms-3" onClick={openPasswordModal(record)}>
                <FontAwesomeIcon fixedWidth icon={solid('key')} />
              </IconButton>
              <Link to={`${record.id}/edit`} className="ms-2">
                <IconButton tooltip="Edit">
                  <FontAwesomeIcon fixedWidth icon={solid('pen')} />
                </IconButton>
              </Link>
              <IconButton tooltip="Delete" className="text-danger ms-2" onClick={openDeleteModal(record)}>
                <FontAwesomeIcon fixedWidth icon={solid('trash')} />
              </IconButton>
            </Space>
          )}
        />
      </Table>
    )
  }, [error, filteredUsers, toggleUser, openPasswordModal, openDeleteModal]);

  return (
    <Container className="my-3">
      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/control-panel" }}>Control Panel</Breadcrumb.Item>
        <Breadcrumb.Item active>User Management</Breadcrumb.Item>
      </Breadcrumb>

      <Row className="mb-3" align="middle">
        <Col md={6}>
          <h5 className="mb-0">Users List</h5>
        </Col>

        <Col flex="1 0" className="text-center px-3">
          <AppSearch placeholder="Search user..." value={searchText} onChange={setSearchText} />
        </Col>

        <Col md={6} className="text-end">
          <Button size="sm" variant="success" as={Link as unknown as CustomBootstrapLinkType} to="/control-panel/users-management/create">
            <FontAwesomeIcon icon={solid('plus')} />
            <span className="ms-2">Register User</span>
          </Button>
        </Col>
      </Row>

      {renderAsyncContent()}

      <Modal
        title={`Change '${user?.username}' Password`}
        open={showPasswordModal}
        onCancel={handleClose}
        afterClose={handleClose}
        footer={null}
      >
        <br />
        <Form form={form} {...layoutProps} onFinish={handleSubmit}>
          <Form.Item name="password" label="Password" rules={[{ required: true, message: 'Please enter new password' }]}>
            <Input.Password placeholder="New user password" />
          </Form.Item>

          <Form.Item
            className="mt-4"
            name="confirmPassword"
            label="Confirm Password"
            rules={[
              { required: true, message: 'Please enter password confirmation' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) return Promise.resolve();
                  return Promise.reject(new Error('The two passwords do not match!'));
                }
              })
            ]}
          >
            <Input.Password placeholder="Confirm user password" />
          </Form.Item>

          <br />

          <Flex justify="space-between">
            <Button size="sm" variant="light" className="border" type="button" onClick={handleClose}>Cancel</Button>
            <Button size="sm" variant="success" type="submit">Confirm</Button>
          </Flex>
        </Form>
      </Modal>

      <Modal
        title="Delete User"
        open={showDeleteModal}
        onCancel={handleClose}
        afterClose={handleClose}
        footer={
          <div className="d-flex justify-content-between mt-3">
            <Button size="sm" variant="light" className="border" onClick={handleClose}>Cancel</Button>
            <Button size="sm" variant="danger" onClick={deleteUser}>
              <FontAwesomeIcon icon={solid('trash')} />
              <span className="ms-2">Delete</span>
            </Button>
          </div>
        }
      >
        <p>Are you sure you want to delete user <b>{user?.username}</b>?</p>

        <Alert type="warning" showIcon message="Remember, this operation cannot be undone" />
      </Modal>
    </Container>
  )
}
